import React, { useState, useEffect } from 'react';
import axios from 'axios';
import API_BASE_URL from '../apiConfig';
import { useAuth } from '../context/AuthContext';
import './FeedbackForm.css';

function FeedbackForm({ onClose, isOpen = true }) {
  const { user } = useAuth();
  const [category, setCategory] = useState('bug');
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [ticketId, setTicketId] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setCategory('bug');
      setTitle('');
      setBody('');
      setError('');
      setSuccess('');
      setTicketId('');
      setLoading(false);
    }
  }, [isOpen]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setTicketId('');
    setLoading(true);
    try {
      const res = await axios.post(`${API_BASE_URL}/api/feedback`, {
        category,
        title: title.trim(),
        body: body.trim(),
      });
      const id = res.data?.ticketId || res.data?.id;
      setTicketId(id || '');
      setSuccess(res.data?.message || 'Thank you. Your feedback was submitted.');
      setTitle('');
      setBody('');
    } catch (err) {
      const msg = err.response?.data?.error || 'Could not submit feedback';
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="feedback-form feedback-form--modal">
      <h2 id="feedback-modal-title">Report a bug or suggestion</h2>
      <p className="feedback-form-hint">
        You are signed in as <strong>{user?.email}</strong>. Each report is tied to your account.
      </p>
      {error && <div className="error-message">{error}</div>}
      {success && (
        <div className="feedback-form-success" role="status">
          <p>{success}</p>
          {ticketId && (
            <p className="feedback-ticket-id">
              <strong>Your ticket ID:</strong> <code>{ticketId}</code>
            </p>
          )}
          {onClose && (
            <button type="button" className="btn btn-primary feedback-form-close-btn" onClick={onClose}>
              Close
            </button>
          )}
        </div>
      )}
      {!success && (
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Type</label>
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              disabled={loading}
              className="feedback-form-select"
            >
              <option value="bug">Bug / defect</option>
              <option value="suggestion">Suggestion</option>
            </select>
          </div>
          <div className="form-group">
            <label htmlFor="feedback-title">Short title</label>
            <input
              id="feedback-title"
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              maxLength={500}
              required
              disabled={loading}
              placeholder="Short title for your issue or suggestion"
            />
          </div>
          <div className="form-group">
            <label htmlFor="feedback-body">Details</label>
            <textarea
              id="feedback-body"
              value={body}
              onChange={(e) => setBody(e.target.value)}
              rows={8}
              minLength={10}
              maxLength={8000}
              required
              disabled={loading}
              placeholder="For a suggestion, describe your idea. For a bug, include steps to reproduce, what you expected, and what happened instead."
            />
          </div>
          <div className="feedback-form-actions">
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? 'Submitting…' : 'Submit feedback'}
            </button>
            {onClose && (
              <button type="button" className="btn btn-secondary" disabled={loading} onClick={onClose}>
                Cancel
              </button>
            )}
          </div>
        </form>
      )}
    </div>
  );
}

export default FeedbackForm;
